import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useVendorManagementCompanySettings } from "~/hooks/vendors/useVendorManagementCompanySettings";
import type { CompanySettingWithItemSetting } from "~/models/CompanySetting";
import type { VendorManagementCompanySetting } from "~/models/VendorManagementCompanySetting";
import { validationSchema } from "~/models/VendorManagementCompanySetting";
import { isSuccess } from "~/utils/api/api_handler";
import { update } from "~/utils/api/vendors/VendorManagementCompanySetting";
import { onInvalidScroll } from "~/utils/FormUtil";
import { VendorManagementCompanySettingForm } from "./VendorManagementCompanySettingForm";

type Props = {
	companySetting: CompanySettingWithItemSetting;
	handleClose: () => void;
};

export const VendorManagementCompanySettingModal: React.FC<Props> = ({
	companySetting,
	handleClose
}: Props) => {
	const { notify } = useNotification();
	const { data: setting, mutate: mutateSetting } =
		useVendorManagementCompanySettings().show(companySetting.companyMasterId);
	const methods = useForm<VendorManagementCompanySetting>({
		mode: "onBlur",
		resolver: yupResolver(validationSchema)
	});
	React.useEffect(() => {
		if (setting) {
			methods.reset(setting);
		}
	}, [setting, methods]);
	const handleModalSubmit = React.useCallback(() => {
		methods.handleSubmit(
			async (submitValue: VendorManagementCompanySetting) => {
				const response = await update(
					companySetting.companyMasterId,
					submitValue
				);
				if (isSuccess(response)) {
					mutateSetting?.();
					handleClose();
					notify({ content: "更新しました。", variant: "success" });
					return;
				}
				handleClose();
				notify({ content: "エラーが発生しました。", variant: "error" });
			},
			(errors) => {
				onInvalidScroll(errors);
			}
		)();
	}, [
		methods,
		companySetting.companyMasterId,
		handleClose,
		notify,
		mutateSetting
	]);
	return (
		<Modalv2 isOpen onClose={handleClose} size="l">
			<ModalHeader title={`${companySetting.name} 設定の更新`} />
			<ModalBody>
				<FormProvider {...methods}>
					<VendorManagementCompanySettingForm />
				</FormProvider>
			</ModalBody>
			<ModalFooter
				leftArea={
					<Button
						onClick={(): void => {
							methods.reset();
							handleClose();
						}}
						variant="secondary"
					>
						保存せず閉じる
					</Button>
				}
				rightArea={
					<Button
						disabled={methods.formState.isSubmitting}
						onClick={handleModalSubmit}
						variant="primary"
					>
						更新
					</Button>
				}
			/>
		</Modalv2>
	);
};
